import React, { useState } from "react";
import { GoldenSourceHelper } from "../../../../app/Switch_Core_React_Standards";
import { SEC_03_AssignmentValidation } from "../Validations/SEC_03_AssignmentValidation"; 

export default function SEC_03_UserMatrixView({ data, loading, availableDivisions = [], availableRoles = [], onAssign, onRevoke }) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [showAddRow, setShowAddRow] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  const [tempDiv, setTempDiv] = useState("");
  const [tempRole, setTempRole] = useState("");

  const handleClear = () => {
    setSelectedIds([]);
    setShowAddRow(false);
    setEditingId(null);
    setTempDiv("");
    setTempRole("");
  };

  const handleConfirm = (isEdit = false) => {
    const validation = SEC_03_AssignmentValidation(tempDiv, tempRole, data);
    if (!validation.isValid && !isEdit) {
      alert(validation.message);
      return;
    }
    if (onAssign) onAssign(tempRole);
    handleClear();
  };

  const handleRevokeSelected = () => {
    const roleIds = selectedIds
      .map((rowId) => {
        const row = data.find((r, i) => (GoldenSourceHelper.get(r, "RoleAssignmentId") || i) === rowId);
        return row ? GoldenSourceHelper.get(row, "RoleId") : null;
      })
      .filter(Boolean);
    if (onRevoke && roleIds.length) onRevoke(roleIds);
    handleClear();
  };

  if (loading) return <div className="p-3 extra-small text-muted italic">Loading security matrix...</div>;

  return (
    <div className="bg-light p-3 border-top border-bottom animate__animated animate__fadeIn">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center gap-3">
          <h6 className="extra-small fw-bold text-uppercase text-primary mb-0">Assigned Authority</h6>
          <button className="btn btn-link p-0 extra-small text-decoration-none text-muted fw-bold" onClick={handleClear}>
            <i className="bi bi-x-circle me-1"></i>CLEAR
          </button>
        </div>
        {selectedIds.length > 0 && (
          <button
            className="btn btn-danger btn-xs fw-bold px-2 py-1 shadow-sm animate__animated animate__pulse"
            onClick={handleRevokeSelected}
          >
            <i className="bi bi-trash3 me-1"></i> REVOKE ({selectedIds.length})
          </button>
        )}
      </div>

      <table className="table table-sm table-borderless mb-0 bg-transparent">
        <thead>
          <tr className="extra-small text-muted text-uppercase">
            <th style={{ width: "40px" }}></th>
            <th>Role Profile</th>
            <th>Resource Scope</th>
            <th className="text-end pe-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => {
            const rowId = GoldenSourceHelper.get(row, "RoleAssignmentId") || i;
            const isEditing = editingId === rowId;
            return (
              <tr key={i} className={`small ${isEditing ? 'bg-white shadow-sm' : ''}`}>
                <td>
                  <input 
                    type="checkbox" 
                    className="form-check-input border-dark border-2 shadow-none" // 🎯 FIXED: Visible high-contrast border
                    checked={selectedIds.includes(rowId)}
                    onChange={() => setSelectedIds(prev => prev.includes(rowId) ? prev.filter(x => x !== rowId) : [...prev, rowId])}
                  />
                </td>
                {isEditing ? (
                  <>
                    <td>
                      <select className="form-select form-select-xs extra-small py-0" value={tempRole} onChange={(e) => setTempRole(e.target.value)}>
                        {availableRoles.map((r, idx) => <option key={idx} value={GoldenSourceHelper.get(r, "RoleId")}>{GoldenSourceHelper.get(r, "RoleName")}</option>)}
                      </select>
                    </td>
                    <td>
                      <select className="form-select form-select-xs extra-small py-0" value={tempDiv} onChange={(e) => setTempDiv(e.target.value)}>
                        {availableDivisions.map((d, idx) => <option key={idx} value={GoldenSourceHelper.get(d, "ResourceId")}>{GoldenSourceHelper.get(d, "ResourceName")}</option>)}
                      </select>
                    </td>
                    <td className="text-end pe-3">
                      <button className="btn btn-xs btn-dark fw-bold px-2" onClick={() => handleConfirm(true)}>SAVE</button>
                    </td>
                  </>
                ) : (
                  <>
                    <td><span className="badge bg-white border text-dark extra-small">{GoldenSourceHelper.get(row, "RoleName")}</span></td>
                    <td className="text-muted">{GoldenSourceHelper.get(row, "ResourceName") || "Global Access"}</td>
                    <td className="text-end pe-3">
                      <div className="d-flex justify-content-end align-items-center gap-2">
                        <span className="extra-small text-muted italic">{GoldenSourceHelper.get(row, "AssignedBy_UserName")}</span>
                        <button className="btn btn-link btn-sm p-0 text-primary" onClick={() => {
                          setEditingId(rowId);
                          setTempDiv(GoldenSourceHelper.get(row, "ResourceId"));
                          setTempRole(GoldenSourceHelper.get(row, "RoleId"));
                        }}>
                          <i className="bi bi-pencil-square"></i>
                        </button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            );
          })}

          {/* 🎯 THE ADD TRIGGER: Matches your working WorkflowHub pattern */}
          {!showAddRow ? (
            <tr>
              <td></td>
              <td colSpan="2" className="extra-small text-muted italic align-middle pt-2">
                Configure new authority assignment...
              </td>
              <td className="text-end pe-3 pt-2">
                <div className="d-flex flex-wrap justify-content-end align-items-center gap-1">
                  <button 
                    type="button"
                    className="btn btn-xs btn-outline-secondary p-0 px-1 border-dotted" 
                    style={{ fontSize: '10px', lineHeight: '1' }}
                    onClick={() => setShowAddRow(true)}
                  >
                    +
                  </button>
                </div>
              </td>
            </tr>
          ) : (
            <tr className="bg-white shadow-sm border">
              <td>
                <button className="btn btn-link btn-sm p-0 text-danger" onClick={() => setShowAddRow(false)}>
                  <i className="bi bi-dash-square"></i>
                </button>
              </td>
              <td>
                <select className="form-select form-select-xs extra-small py-0" value={tempRole} onChange={(e) => setTempRole(e.target.value)}>
                  <option value="">-- Role --</option>
                  {availableRoles.map((r, idx) => <option key={idx} value={GoldenSourceHelper.get(r, "RoleId")}>{GoldenSourceHelper.get(r, "RoleName")}</option>)}
                </select>
              </td>
              <td>
                <select className="form-select form-select-xs extra-small py-0" value={tempDiv} onChange={(e) => setTempDiv(e.target.value)}>
                  <option value="">-- Division --</option>
                  {availableDivisions.map((d, idx) => <option key={idx} value={GoldenSourceHelper.get(d, "ResourceId")}>{GoldenSourceHelper.get(d, "ResourceName")}</option>)}
                </select>
              </td>
              <td className="text-end pe-3">
                {tempRole && tempDiv && (
                  <button className="btn btn-xs btn-primary fw-bold px-3" onClick={() => handleConfirm(false)}>
                    CONFIRM
                  </button>
                )}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}